import NextLink from 'next/link'
import {
  KeyboardEvent,
  MouseEvent,
  ReactNode,
  useCallback,
  useContext,
} from 'react'
import { SiteContext } from '@lib/site-context'
import { PageHeroContext } from '@lib/page-hero-context'

interface BaseInternalLinkProps {
  href: string
  role?: string
  'aria-label'?: string
  onClick?: () => void
  className?: string
  children?: ReactNode
}

const BaseInternalLink = ({
  href,
  role,
  'aria-label': ariaLabel,
  onClick,
  className,
  children,
}: BaseInternalLinkProps) => {
  const { sourcePage, targetPage } = useContext(PageHeroContext)
  const { togglePageTransition } = useContext(SiteContext)

  /**
   * Manually navigates to URL, if needed.
   */
  const navigateTo = useCallback(
    (
      event: MouseEvent<HTMLAnchorElement> | KeyboardEvent<HTMLAnchorElement>,
      url: string,
    ) => {
      if (!sourcePage || !targetPage) {
        return
      }

      event.preventDefault()

      togglePageTransition(true)
      window.location.href = url
    },
    [sourcePage, targetPage, togglePageTransition],
  )

  const handleClick = useCallback(
    (event: MouseEvent<HTMLAnchorElement>) => {
      navigateTo(event, href)

      if (onClick) {
        onClick()
      }
    },
    [href, navigateTo, onClick],
  )

  const handleKeyDown = useCallback(
    (event: KeyboardEvent<HTMLAnchorElement>) => {
      navigateTo(event, href)

      if (onClick) {
        onClick()
      }
    },
    [href, navigateTo, onClick],
  )

  return (
    <NextLink
      href={href}
      role={role}
      aria-label={ariaLabel}
      onClick={handleClick}
      onKeyDown={handleKeyDown}
      className={className}
    >
      {children}
    </NextLink>
  )
}

export default BaseInternalLink
